import React, { useEffect, useRef, useState } from 'react';
import { bulkProductService } from '../services/bulkProductService';

type PackagingOption = {
  id: number;
  name: string;
  sku_number: string;
  vendor?: string | null;
};

type PackagingBulkPickerProps = {
  kind: 'box' | 'container';
  label: string;
  input_id: string;
  value?: number;
  /** Container name saved on the product. Shown in the field and kept while the user edits it. */
  stored_label?: string | null;
  onChange: (id: number | undefined, item?: PackagingOption, typed_name?: string) => void;
};

const PAGE_SIZE = 20;

export const PackagingBulkPicker: React.FC<PackagingBulkPickerProps> = ({
  kind,
  label,
  input_id,
  value,
  stored_label,
  onChange,
}) => {
  const root_ref = useRef<HTMLDivElement>(null);
  const request_id = useRef(0);
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<PackagingOption[]>([]);
  const [page, setPage] = useState(1);
  const [total_pages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected_label, setSelectedLabel] = useState('');

  useEffect(() => {
    if (!value) {
      setSelectedLabel('');
      return;
    }
    let cancelled = false;
    bulkProductService.getPackagingOption(value, kind).then((item) => {
      if (cancelled || !item) return;
      setSelectedLabel(item.name);
    }).catch(() => {
      if (!cancelled) setSelectedLabel(`#${value}`);
    });
    return () => {
      cancelled = true;
    };
  }, [kind, value]);

  useEffect(() => {
    if (!open) return;
    const close_on_outside = (event: MouseEvent) => {
      if (!root_ref.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close_on_outside);
    return () => document.removeEventListener('mousedown', close_on_outside);
  }, [open]);

  const load_page = async (next_page: number, term: string, append: boolean) => {
    const id = ++request_id.current;
    setLoading(true);
    try {
      const result = await bulkProductService.searchPackagingOptions(kind, term, next_page, PAGE_SIZE);
      if (id !== request_id.current) return;
      setItems((current) => (append ? [...current, ...result.items] : result.items));
      setPage(result.pagination.currentPage || next_page);
      setTotalPages(result.pagination.totalPages || 0);
    } catch (error) {
      console.error('Packaging option search failed', error);
      if (id !== request_id.current) return;
      if (!append) setItems([]);
    } finally {
      if (id === request_id.current) setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setQuery(null);
  };

  const visible_label = (value ? selected_label || stored_label : stored_label) || '';
  const shown = query ?? visible_label;

  const open_with_saved_text = () => {
    const saved = visible_label;
    setQuery(saved);
    setOpen(true);
    load_page(1, saved, false);
  };

  return (
    <div ref={root_ref} className="form-group" style={{ position: 'relative', zIndex: open ? 30 : 1 }}>
      <label htmlFor={input_id}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          id={input_id}
          type="text"
          value={shown}
          placeholder={`Type a ${kind} name, or search by SKU or vendor`}
          autoComplete="off"
          onMouseDown={() => {
            if (!open) open_with_saved_text();
          }}
          onFocus={() => {
            if (!open) open_with_saved_text();
          }}
          onChange={(event) => {
            const term = event.target.value;
            setQuery(term);
            setOpen(true);
            load_page(1, term, false);
            onChange(undefined, undefined, term);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              close();
            }
          }}
          style={{
            position: 'relative',
            zIndex: 2,
            padding: '0.75rem',
            fontSize: '0.875rem',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            width: '100%',
            boxSizing: 'border-box',
            backgroundColor: 'white',
          }}
        />
        {open && (
          <div
            role="listbox"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1,
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              marginTop: '0.25rem',
              maxHeight: '220px',
              overflowY: 'auto',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            {items.map((item) => (
              <div
                key={item.id}
                role="option"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(item.id, item);
                  setSelectedLabel(item.name);
                  setQuery(item.name);
                  close();
                }}
                style={{
                  padding: '0.75rem',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f1f3f4',
                  fontSize: '0.875rem',
                }}
              >
                <div style={{ fontWeight: 500 }}>{item.name}</div>
                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                  SKU: {item.sku_number}
                  {item.vendor ? ` • ${item.vendor}` : ''}
                </div>
              </div>
            ))}
            {items.length === 0 && !loading && (
              <div style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#666' }}>
                No {kind} products match
              </div>
            )}
            {loading && items.length === 0 && (
              <div style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#666' }}>Loading…</div>
            )}
            {page < total_pages && (
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => load_page(page + 1, shown, true)}
                style={{ width: '100%', padding: '0.5rem', fontSize: '0.875rem' }}
              >
                {loading ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        )}
      </div>
      {value && (
        <button
          type="button"
          onClick={() => {
            onChange(undefined);
            setQuery('');
            setSelectedLabel('');
            close();
          }}
          style={{ marginTop: '0.35rem', fontSize: '0.75rem' }}
        >
          Clear
        </button>
      )}
    </div>
  );
};
